import React, { useEffect } from "react";
import { Box, Center, Flex, Spinner, Text } from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { JeopardyStates } from "types";
import { SignIn } from "./SignIn";
import { Waiting } from "./Waiting";
import { CategorySelection } from "./CategorySelection";
import { BuzzIn } from "./BuzzIn";
import { PlayerAnswering } from "./PlayerAnswering";
import { ChallengeVoting } from "./ChallengeVoting";
import { useMobileOrientation } from "react-device-detect";
import { motion } from "framer-motion";

import InlineSVG from "react-inlinesvg";
import { useAnimateScore } from "../../hooks/useAnimateScore";
import { TextFit } from "../TextFit";
import { CollectWager } from "./CollectWager";
import { useParams } from "react-router-dom";

const SVG = InlineSVG as any;

const pageContent = (currentState: JeopardyStates) => {
  switch (currentState) {
    case JeopardyStates.WaitingForPlayers:
      return <Waiting />;
    case JeopardyStates.CategorySelection:
      return <CategorySelection />;
    case JeopardyStates.WaitingForBuzz:
      return <BuzzIn />;
    case JeopardyStates.PlayerAnswering:
      return <PlayerAnswering />;
    case JeopardyStates.DailyDoubleWager:
      return <CollectWager />;
    case JeopardyStates.ChallengeVoting:
      return <ChallengeVoting />;
    case JeopardyStates.CollectFinalJeopardyWagers:
      return <CollectWager />;
    case JeopardyStates.MakeFinalJeopardyGuesses:
      return <PlayerAnswering />;
    case JeopardyStates.RevealFinalJeopardyAnswer:
      return <Text textStyle="clue">Game Over</Text>;
    default:
      return <Text textStyle="clue">Unknown State</Text>;
  }
};

export interface PlayerProps {
  /** If true, renders the host bar at the top */
  isHost?: boolean;
  /** Optional render prop for the host bar - receives isLandscape */
  hostBar?: (isLandscape: boolean) => React.ReactNode;
}

export const Player: React.FC<PlayerProps> = ({ isHost = false, hostBar }) => {
  const { gameId: urlGameId } = useParams<{ gameId: string }>();
  const { gameState, currentPlayer, activeCategory, setGameId, isLoadingGameState } = useGame();
  const { isLandscape } = useMobileOrientation();
  const { score } = useAnimateScore(currentPlayer?.score || 0);

  // Set game ID from URL parameter
  useEffect(() => {
    if (urlGameId) {
      setGameId(urlGameId);
    }
  }, [urlGameId, setGameId]);

  return (
    <Center height="100%" width="100%" background="jeopardy.blue">
      {currentPlayer && gameState ? (
        <Flex
          alignItems="center"
          height="100%"
          width="100%"
          flexDir="column"
          justifyContent="space-between"
        >
          {/* Host bar - rendered only when isHost and hostBar are provided */}
          {isHost && hostBar && hostBar(isLandscape)}

          {!isLandscape && (
            <Flex
              py="10px"
              alignItems="center"
              justifyContent="center"
              gap="12px"
            >
              <Box width="50%">
                {currentPlayer.nameSvg && <SVG src={currentPlayer.nameSvg} />}
              </Box>
              <Text
                width="50%"
                py="10px"
                textStyle="category"
                textAlign="center"
                fontSize="40px"
                whiteSpace="nowrap"
              >
                <motion.span>{score}</motion.span>
              </Text>
            </Flex>
          )}
          {[
            JeopardyStates.WaitingForBuzz,
            JeopardyStates.PlayerAnswering,
          ].includes(gameState.currentState) &&
            activeCategory && (
              <TextFit
                width="80%"
                height="auto"
                textAlign="center"
                as={Center}
                textStyle="category"
              >
                {activeCategory.name}
              </TextFit>
            )}
          <Center width="100%" flexGrow={1}>
            {pageContent(gameState.currentState)}
          </Center>
        </Flex>
      ) : currentPlayer && isLoadingGameState ? (
        <Center height="100%" width="100%" flexDir="column" gap={4}>
          <Spinner size="xl" color="white" thickness="4px" />
          <Text textStyle="clue" fontSize="lg">Joining game...</Text>
        </Center>
      ) : (
        <SignIn />
      )}
    </Center>
  );
};
